import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  AppBar as MuiAppBar,
  Typography,
  Divider,
  Tooltip,
  Avatar,
} from "@mui/material";

import {
  Receipt as ReceiptIcon,
  Settings as SettingsIcon,
  DonutSmall as DonutIcon,
  ExpandMore as ExpandMoreIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { isAfter, startOfDay } from "date-fns";

import { SubmenuKeys, useSidebar } from "context/SidebarContext";

import TermsModal from "components/Sidebar/components/TermsModal";
import TermsModalOperator from "components/Sidebar/components/TermsModalOperator";

import { getEnvironmentVariables } from "config/appConfig";
import { useAppDispatch } from "store/hooks";
import { useAuth } from "context/AuthContext";
import { clarityManager } from "libs/clarity/clarity";

import * as Styled from "./styles";

interface SidebarItems {
  name: string;
  key: SubmenuKeys;
  isAccordion: boolean;
  path: string;
  items: { name: string; path: string }[] | null;
  icon: JSX.Element;
}

const Sidebar = () => {
  const {
    isSidebarMaximized,
    isSubmenuOpen,
    setIsSubmenuOpen,
    handleToggleSidebar,
  } = useSidebar();

  const { signOut, profileData, dealerName, terms } = useAuth();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const [anchorElUser, setAnchorElUser] = useState<
    (EventTarget & HTMLElement) | null
  >(null);

  const [isTermsVisible, setIsTermsVisible] = useState(false);

  const { logoFull } = getEnvironmentVariables();

  const dealerInfo = JSON.parse(
    localStorage.getItem("@ctech:dealerInfo") || "{}",
  );

  const scheduleEnabled = dealerInfo?.attributes?.schedulingEnabled;

  const handleSignOut = () => {
    dispatch(signOut());
    navigate("/");
  };

  const sidebarItems: SidebarItems[] = [
    {
      name: t("orders"),
      key: "orders",
      isAccordion: true,
      path: "",
      items: [
        { name: t("manageOrders"), path: "orders/manage" },
        ...(scheduleEnabled
          ? [{ name: t("scheduleOrders"), path: "orders/schedule" }]
          : []),
        { name: t("trackingMap"), path: "orders/rastreamento" },
        { name: t("messages"), path: "orders/chat" },
      ],
      icon: <ReceiptIcon />,
    },
    {
      name: t("settings"),
      key: "settings",
      path: "",
      isAccordion: true,
      items: [
        {
          name: t("productsAndCoverage"),
          path: "settings/products-and-coverage",
        },
        { name: t("storeInformation"), path: "/dealers/info" },
        { name: t("manageUsers"), path: "/settings/users" },
      ],
      icon: <SettingsIcon />,
    },
    {
      name: t("reports"),
      key: "reports",
      isAccordion: true,
      path: "//",
      items: [
        { name: t("dashboard"), path: "/reports/dashboard" },
        { name: t("reportsSales"), path: "/reports/sales" },
        { name: t("ordersHistory"), path: "/reports/order-history" },
        { name: t("payments"), path: "/reports/payments" },
      ],
      icon: <DonutIcon />,
    },
  ];

  const handleChangeSubmenuVisibility = (submenuName: SubmenuKeys) => {
    setIsSubmenuOpen((state) => ({
      ...state,
      [submenuName]: !state[submenuName],
    }));
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const getAvatarImgOrStringAvatar = (
    name: string,
    photoUrl?: string | null,
  ) => {
    if (photoUrl)
      return {
        src: photoUrl,
      };

    return {
      children: `${name.split(" ")[0][0]}`,
    };
  };

  const handleChangeTermsModalVisibility = useCallback(() => {
    setIsTermsVisible((state) => !state);
  }, []);

  useEffect(() => {
    const lastShownDate = localStorage.getItem("@ctech:shownTermsDate");

    const notShownTermsToday = isAfter(
      startOfDay(new Date()),
      startOfDay(new Date(lastShownDate || "")),
    );

    if (
      profileData.profile === "administrator" &&
      !!terms &&
      !terms?.acceptedAt &&
      (notShownTermsToday || !lastShownDate)
    ) {
      setIsTermsVisible(true);
    }
  }, [terms, profileData.profile]);

  return (
    <Styled.Container>
      <TermsModal
        visible={isTermsVisible}
        onClose={handleChangeTermsModalVisibility}
        showActions={
          !terms?.acceptedAt && profileData.profile === "administrator"
        }
        canClose={
          !isAfter(
            startOfDay(new Date()),
            startOfDay(new Date(terms?.agreement.acceptUntil || "")),
          )
        }
        alreadyAccepted={!!terms?.acceptedAt}
      />
      <TermsModalOperator
        visible={
          isAfter(
            startOfDay(new Date()),
            startOfDay(new Date(terms?.agreement.acceptUntil || "")),
          ) &&
          profileData.profile !== "administrator" &&
          !terms?.acceptedAt
        }
      />
      <MuiAppBar position="fixed">
        <Styled.AppBar>
          <Styled.ToolbarLeftContainer>
            <Styled.HamburgerButton
              color="inherit"
              onClick={handleToggleSidebar}
              edge="start"
            >
              <MenuIcon />
            </Styled.HamburgerButton>

            <Styled.LogoFullImg src={logoFull} />
          </Styled.ToolbarLeftContainer>
          <Styled.UserMenuContainer>
            <Tooltip title={t("openSettings")}>
              <Styled.AvatarIconButton onClick={handleOpenUserMenu}>
                <Avatar
                  alt={profileData.name}
                  {...getAvatarImgOrStringAvatar(
                    profileData.name,
                    profileData.imageUrl,
                  )}
                />
              </Styled.AvatarIconButton>
            </Tooltip>
            <Styled.MenuContainer
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {/*       <Styled.UserMenuItem onClick={() => {}}>
                <PersonIcon />
                <Typography textAlign="center">{t("myAccount")}</Typography>
              </Styled.UserMenuItem> */}

              <Styled.UserMenuItem onClick={handleSignOut}>
                <LogoutIcon />
                <Typography textAlign="center">{t("logout")}</Typography>
              </Styled.UserMenuItem>
            </Styled.MenuContainer>
          </Styled.UserMenuContainer>
        </Styled.AppBar>
      </MuiAppBar>
      <Styled.DrawerNav
        variant="persistent"
        anchor="left"
        open={isSidebarMaximized}
      >
        <Styled.DrawerHeaderContainer>
          <Typography variant="h6" noWrap component="div">
            {dealerName.toUpperCase()}
          </Typography>
        </Styled.DrawerHeaderContainer>
        <Divider />
        {sidebarItems.map((sidebarItem) => {
          if (sidebarItem.isAccordion) {
            return (
              <React.Fragment key={sidebarItem.name}>
                <Styled.AccordionContainer
                  expanded={isSubmenuOpen[sidebarItem.key]}
                >
                  <Styled.AccordionButton
                    expandIcon={<ExpandMoreIcon />}
                    onClick={() =>
                      handleChangeSubmenuVisibility(sidebarItem.key)
                    }
                  >
                    <Styled.AccordionTextContainer>
                      {sidebarItem.icon}
                      <Styled.TextItem variant="body1" noWrap>
                        {sidebarItem.name}
                      </Styled.TextItem>
                    </Styled.AccordionTextContainer>
                  </Styled.AccordionButton>
                  <Styled.AccordionItemsContainer>
                    {sidebarItem.items?.map((item, index) => {
                      return (
                        <React.Fragment key={item.name}>
                          {index === 0 && <Divider />}
                          <Styled.AccordionSubButton
                            to={item.path}
                            isCurrentPage={pathname.includes(item.path)}
                            onClick={() => {
                              if (item.path === "/reports/sales") {
                                clarityManager.trackEvent({
                                  name: "menu_click_report_sales",
                                });
                              }
                            }}
                          >
                            <Styled.AccordionTextContainer>
                              <Styled.TextSubItem variant="body2" noWrap>
                                {item.name}
                              </Styled.TextSubItem>
                            </Styled.AccordionTextContainer>
                          </Styled.AccordionSubButton>
                          {index !==
                            (sidebarItem.items?.length as number) - 1 && (
                            <Divider />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </Styled.AccordionItemsContainer>
                </Styled.AccordionContainer>
                <Divider />
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={sidebarItem.name}>
              <Styled.AccordionSubButton
                to={sidebarItem.path}
                isCurrentPage={pathname.includes(sidebarItem.path)}
              >
                <Styled.AccordionTextContainer>
                  {sidebarItem.icon}
                  <Typography variant="body1" noWrap component="div">
                    {sidebarItem.name}
                  </Typography>
                </Styled.AccordionTextContainer>
              </Styled.AccordionSubButton>
              <Divider />
            </React.Fragment>
          );
        })}
        {terms && (
          <Styled.Terms onClick={handleChangeTermsModalVisibility}>
            Termos e Condições
          </Styled.Terms>
        )}
      </Styled.DrawerNav>
      <Styled.Main isSidebarMaximized={isSidebarMaximized}>
        <Outlet />
      </Styled.Main>
    </Styled.Container>
  );
};

export default Sidebar;
